import {
    getAvgResolutionTimeByPriority,
    getOverdueTicketsByEmployee,
    getStatisticalTicketStatusSummary,
    getTicketStatusCountByPeriod,
    getTopTicketHandlers,
    getSLACompliance,
    getCustomerSatisfaction,
    getTicketsByType,
    getTicketsByEmployeeAndType,
} from '@/modules/Ticket/services'
import { formatDate } from '@/modules/Ticket/types/utils'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Dayjs } from 'dayjs'
import TicketStatusPieChart from './Charts/TicketStatusPieChart'
import {
    OverdueTicketsByEmployee,
    TicketAvgPriority,
    TicketHandler,
    TicketStatusPeriod,
    TicketStatusSummary,
    SLACompliance,
    CustomerSatisfaction,
    TicketsByType,
    TicketsByEmployeeAndType,
} from '@/modules/Ticket/types/response'
import TicketTimeResponseChart from './Charts/TicketTimeResponseChart'
import TopTicketHanler from './Charts/TopTicketHanler'
import { useEffect, useState } from 'react'
import TicketOverDuedateChart from './Charts/TicketOverDuedateChart'
import TicketColumChart from './Charts/TicketColumChart'
import SLAComplianceChart from './Charts/SLAComplianceChart'
import CustomerSatisfactionChart from './Charts/CustomerSatisfactionChart'
import TicketsByTypeChart from './Charts/TicketsByTypeChart'
import TicketProcessingTable from './Charts/TicketProcessingTable'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface StatisticalProps {
    filterTime: [Dayjs | null, Dayjs | null]
}
const Statistical: React.FC<StatisticalProps> = ({ filterTime }) => {
    const [start_date, end_date] = Array.isArray(filterTime)
        ? filterTime
        : [null, null]
    const startDate = start_date ? formatDate(start_date) : ''
    const endDate = end_date ? formatDate(end_date) : ''
    const [chartPeriod, setChartPeriod] = useState<
        'day' | 'week' | 'month' | 'quarter'
    >('day')
    const { data, renderState, refetch } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKET_DASHBOARD, startDate, endDate],
        queryFn: async () => {
            const [
                statusSummary,
                avgResolution,
                topTicketHandlers,
                overdueTickets,
                ticketStatusPeriod,
                slaCompliance,
                customerSatisfaction,
                ticketsByType,
                ticketsByEmployeeAndType,
            ] = await Promise.allSettled([
                getStatisticalTicketStatusSummary({
                    start_date: startDate || '',
                    end_date: endDate || '',
                }),
                getAvgResolutionTimeByPriority({
                    start_date: startDate || '',
                    end_date: endDate || '',
                }),
                getTopTicketHandlers({
                    start_date: startDate || '',
                    end_date: endDate || '',
                }),
                getOverdueTicketsByEmployee({
                    start_date: startDate || '',
                    end_date: endDate || '',
                }),
                getTicketStatusCountByPeriod({
                    start_date: startDate || '',
                    end_date: endDate || '',
                    period: chartPeriod !== 'quarter' ? chartPeriod : 'month',
                }),
                getSLACompliance({
                    start_date: startDate || '',
                    end_date: endDate || '',
                }),
                getCustomerSatisfaction({
                    start_date: startDate || '',
                    end_date: endDate || '',
                }),
                getTicketsByType({
                    start_date: startDate || '',
                    end_date: endDate || '',
                }),
                getTicketsByEmployeeAndType({
                    start_date: startDate || '',
                    end_date: endDate || '',
                }),
            ])
            return {
                statusSummary:
                    statusSummary.status === 'fulfilled'
                        ? statusSummary.value
                        : { results: [] },
                avgResolution:
                    avgResolution.status === 'fulfilled'
                        ? avgResolution.value
                        : { results: [] },
                topTicketHandlers:
                    topTicketHandlers.status === 'fulfilled'
                        ? topTicketHandlers.value
                        : { results: [] },
                overdueTickets:
                    overdueTickets.status === 'fulfilled'
                        ? overdueTickets.value
                        : { results: [] },
                ticketStatusPeriod:
                    ticketStatusPeriod.status === 'fulfilled'
                        ? ticketStatusPeriod.value
                        : { results: [] },
                slaCompliance:
                    slaCompliance.status === 'fulfilled'
                        ? slaCompliance.value
                        : { result: null },
                customerSatisfaction:
                    customerSatisfaction.status === 'fulfilled'
                        ? customerSatisfaction.value
                        : { result: null },
                ticketsByType:
                    ticketsByType.status === 'fulfilled'
                        ? ticketsByType.value
                        : { results: [] },
                ticketsByEmployeeAndType:
                    ticketsByEmployeeAndType.status === 'fulfilled'
                        ? ticketsByEmployeeAndType.value
                        : { results: [] },
            }
        },
    })
    useEffect(() => {
        if (startDate && endDate) {
            refetch()
        }
    }, [startDate, endDate, refetch])
    const ticket_status_summary: TicketStatusSummary[] =
        data?.statusSummary?.results || []
    const avg_resolution_time: TicketAvgPriority[] =
        data?.avgResolution?.results || []
    const top_ticket_handlers: TicketHandler[] =
        data?.topTicketHandlers?.results || []
    const overdue_tickets_by_employee: OverdueTicketsByEmployee[] =
        data?.overdueTickets?.results || []
    const ticket_status_count_by_period: TicketStatusPeriod[] =
        data?.ticketStatusPeriod?.results || []
    const sla_compliance: SLACompliance | null =
        data?.slaCompliance?.result || null
    const customer_satisfaction: CustomerSatisfaction | null =
        data?.customerSatisfaction?.result || null
    const tickets_by_type: TicketsByType[] = data?.ticketsByType?.results || []
    const tickets_by_employee_and_type: TicketsByEmployeeAndType[] =
        data?.ticketsByEmployeeAndType?.results || []

    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    return (
        <>
            {/* Row 1: Main charts */}
            <div className="grid lg:grid-cols-12 grid-cols-1 gap-2 mb-2">
                <TicketColumChart
                    ticket_status_count_by_period={
                        ticket_status_count_by_period
                    }
                    value={chartPeriod}
                    setValue={setChartPeriod}
                    filterTime={filterTime}
                />
                <TicketStatusPieChart
                    ticket_status_summary={ticket_status_summary}
                />
                <TicketTimeResponseChart
                    avg_resolution_time_by_priority={avg_resolution_time}
                />
            </div>

            {/* Row 2: Employee performance */}
            <div className="grid lg:grid-cols-12 grid-cols-1 gap-2 mb-2">
                <TopTicketHanler top_ticket_handlers={top_ticket_handlers} />
                <TicketOverDuedateChart
                    overdue_tickets_by_employee={overdue_tickets_by_employee}
                />
            </div>

            {/* Row 3: SLA, Satisfaction, Type */}
            <div className="grid lg:grid-cols-12 md:grid-cols-2 grid-cols-1 gap-2 mb-2">
                <SLAComplianceChart slaCompliance={sla_compliance} />
                <CustomerSatisfactionChart
                    customerSatisfaction={customer_satisfaction}
                />
                <TicketsByTypeChart ticketsByType={tickets_by_type} />
            </div>

            {/* Row 4: Ticket Processing Table by Employee */}
            <div className="grid grid-cols-12 gap-2 mb-2">
                <TicketProcessingTable
                    ticketsByEmployeeAndType={tickets_by_employee_and_type}
                    ticketsByType={tickets_by_type}
                    startDate={startDate}
                    endDate={endDate}
                />
            </div>
        </>
    )
}
export default Statistical
